/**
 * BarmetHub — App Router
 * Updated for BHM-HMS-REQ-032: new navigation structure, Ajustes, Setup Center.
 */
import UnifiedAuditPage from './modules/UnifiedAuditPage';
import UsersPage from './modules/UsersPage';
import NexusPage from './modules/NexusPage';
import PermissionsPage from './modules/PermissionsPage';
import { lazy, Suspense, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './stores/authStore';
import Layout from './components/Layout';
import { modules, legacyModuleAliases } from './modules/registry';
import ModulePage from './modules/ModulePage';
import SystemPage from './modules/SystemPage';
import IntegrationsCatalog from './modules/IntegrationsCatalog';
import SetupCenter from './components/setup-center/SetupCenter';
import DirectorDashboardPage from './pages/DirectorDashboardPage';
import MarketingPage from './pages/MarketingPage';
import { ShareSecretPanel } from './components/ShareSecretPanel';
import { NexusDayPanel } from './components/NexusDayPanel';
import { NexusTeamPanel } from './components/NexusTeamPanel';
import { LaborCalendarPanel } from './components/LaborCalendarPanel';
import EmployeeDashboardPage from './pages/EmployeeDashboardPage';

/* ── Lazy-loaded pages ────────────────────────────────── */
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const RecoverAccessPage = lazy(() => import('./pages/RecoverAccessPage'));
const RecoverySetupPage = lazy(() => import('./pages/RecoverySetupPage'));
const RecoveryExecutePage = lazy(() => import('./pages/RecoveryExecutePage'));
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallbackPage'));
const ActivationPage = lazy(() => import('./pages/ActivationPage'));
const VaultPage = lazy(() => import('./pages/VaultPage'));
const ItemDetailPage = lazy(() => import('./pages/ItemDetailPage'));
const ItemEditPage = lazy(() => import('./pages/ItemEditPage'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const MCPIntegrationsPage = lazy(() => import('./pages/MCPIntegrationsPage'));
const SharesPage = lazy(() => import('./pages/SharesPage'));
const DevicesPage = lazy(() => import('./pages/DevicesPage'));
const VersionsPage = lazy(() => import('./pages/VersionsPage'));
import RequireModulePermission from './components/RequireModulePermission';
import RequireSuperAdmin from './components/RequireSuperAdmin';
const FactoryPromptsPage = lazy(() => import('./pages/FactoryPromptsPage'));
const FactoryIaPage = lazy(() => import('./pages/FactoryIaPage'));
const FactoryTracePage = lazy(() => import('./pages/FactoryTracePage'));

// New pages for BHM-HMS-REQ-032
const MiTrabajoPage = lazy(() => import('./pages/MiTrabajoPage'));
const NexusWorkPage = lazy(() => import('./pages/NexusWorkPage'));
const NexusAdminPage = lazy(() => import('./pages/NexusAdminPage'));
const ClasificarCredencialesPage = lazy(() => import('./pages/ClasificarCredencialesPage'));
const ProyectosPageNew = lazy(() => import('./pages/ProyectosPage'));
const ProyectoDetallePage = lazy(() => import('./pages/ProyectoDetallePage'));
const JornadaPage = lazy(() => import('./pages/JornadaPage'));
const ControlTiemposPage = lazy(() => import('./pages/ControlTiemposPage'));
const TiempoPage = lazy(() => import('./pages/TiempoPage'));
const DashboardsClientesPage = lazy(() => import('./pages/DashboardsClientesPage'));
const RevisionTiemposPage = lazy(() => import('./pages/RevisionTiemposPage'));
const AjustesPage = lazy(() => import('./pages/AjustesPage'));
const MiCuentaPage = lazy(() => import('./pages/MiCuentaPage'));
const SeguridadSistemaPage = lazy(() => import('./pages/SeguridadSistemaPage'));
const RegistroActividadPage = lazy(() => import('./pages/RegistroActividadPage'));
const EstadoSistemaPage = lazy(() => import('./pages/EstadoSistemaPage'));
const IntegracionesSettingsPage = lazy(() => import('./pages/IntegracionesSettingsPage'));
const AccesosEmpleadosPage = lazy(() => import('./pages/AccesosEmpleadosPage'));
const PlanningPage = lazy(() => import('./pages/PlanningPage'));

/* ── Loading fallback ─────────────────────────────────── */
function LoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-outline-variant border-t-[#FF5E1E]" />
        <p className="text-sm text-on-surface-variant">Cargando...</p>
      </div>
    </div>
  );
}

/* ── Protected route wrapper ──────────────────────────── */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/* ── App ──────────────────────────────────────────────── */
export default function App() {
  const [setupOpen, setSetupOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isSA = user?.role === 'owner';

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          className: '!bg-surface-container-high !text-on-surface !shadow-lg !border !border-outline-variant',
          duration: 4000,
          success: { iconTheme: { primary: '#10B981', secondary: '#0a0e18' } },
          error: { iconTheme: { primary: '#F43F5E', secondary: '#0a0e18' } },
        }}
      />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/recuperar-acceso" element={<RecoverAccessPage />} />
          <Route path="/recovery/execute" element={<RecoveryExecutePage />} />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />
          <Route path="/activar-cuenta/:token" element={<ActivationPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout onOpenSetup={() => setSetupOpen(true)} />
              </ProtectedRoute>
            }
          >
            {/* PRINCIPAL — Role-based dashboard */}
            <Route path="dashboard" element={isSA ? <DirectorDashboardPage /> : <EmployeeDashboardPage />} />
            <Route index element={isSA ? <DirectorDashboardPage /> : <EmployeeDashboardPage />} />
            <Route path="mi-trabajo" element={<RequireModulePermission permission="TASKS_VIEW" anyOf={['TIME_LOG']}><MiTrabajoPage /></RequireModulePermission>} />
            <Route path="proyectos" element={<RequireModulePermission permission="PROJECTS_VIEW"><ProyectosPageNew /></RequireModulePermission>} />
            <Route path="proyectos/:id" element={<RequireModulePermission permission="PROJECTS_VIEW" anyOf={['TASKS_VIEW', 'TASKS_CREATE', 'TIME_LOG']}><ProyectoDetallePage /></RequireModulePermission>} />
            <Route path="jornada" element={<RequireModulePermission permission="TIME_VIEW_OWN"><JornadaPage /></RequireModulePermission>} />
            <Route path="control-tiempos" element={<RequireSuperAdmin><ControlTiemposPage /></RequireSuperAdmin>} />
            <Route path="operaciones/tiempo" element={<RequireModulePermission permission="TIME_VIEW_OWN"><TiempoPage /></RequireModulePermission>} />
            <Route path="operaciones/marketing" element={<RequireModulePermission permission="marketing.read"><MarketingPage /></RequireModulePermission>} />
            <Route path="operaciones/revision-tiempos" element={<RequireModulePermission permission="TIME_REVIEW"><RevisionTiemposPage /></RequireModulePermission>} />
            <Route path="planificacion" element={<RequireModulePermission permission="TASKS_VIEW"><PlanningPage /></RequireModulePermission>} />

            {/* REQ-059 — UIs DIR-075 */}
            <Route path="compartir-secreto" element={<RequireModulePermission permission="LASSPASS_VIEW"><ShareSecretPanel /></RequireModulePermission>} />
            <Route path="nexus/dia" element={<RequireModulePermission permission="NEXUS_USE"><NexusDayPanel /></RequireModulePermission>} />
            <Route path="nexus/equipo" element={<RequireSuperAdmin><NexusTeamPanel userRole={isSA ? 'SUPERADMIN' : 'EMPLOYEE'} /></RequireSuperAdmin>} />
            <Route path="calendario-laboral" element={<RequireModulePermission permission="TIME_VIEW_OWN"><LaborCalendarPanel userRole={isSA ? 'SUPERADMIN' : 'EMPLOYEE'} /></RequireModulePermission>} />
            <Route path="dashboards-clientes" element={<RequireSuperAdmin><DashboardsClientesPage /></RequireSuperAdmin>} />

            {/* OPERACIONES */}
            {modules.filter(m => m.group === 'operations' && m.navigation).map(m => (
              <Route key={m.id} path={m.route.slice(1)} element={<ModulePage module={m} />} />
            ))}

            {/* AUTOMATIZACIÓN */}
            <Route path="automatizacion/workflows" element={<ModulePage module={modules.find(m => m.id === 'automations')!} />} />
            <Route path="automatizacion/ia" element={<NexusPage title="IA / Agentes" />} />

            {/* NEXUS CORE */}
            <Route path="nexus" element={<RequireModulePermission permission="NEXUS_USE" anyOf={['NEXUS_ADMIN']}><NexusWorkPage /></RequireModulePermission>} />
            <Route path="nexus/admin" element={<RequireModulePermission permission="NEXUS_ADMIN"><NexusAdminPage /></RequireModulePermission>} />
            <Route path="nexus/hermes" element={<NexusPage title="Runtime de agentes" />} />
            <Route path="nexus/agentes" element={<NexusPage title="Agentes" />} />
            <Route path="nexus/skills" element={<NexusPage title="Skills" />} />
            <Route path="nexus/mcp" element={<MCPIntegrationsPage />} />
            <Route path="nexus/tools" element={<NexusPage title="Tools" />} />

            {/* RECURSOS */}
            <Route path="recursos/lasspass" element={<RequireModulePermission permission="LASSPASS_VIEW"><VaultPage /></RequireModulePermission>} />
            <Route path="recursos/lasspass/clasificar" element={<RequireModulePermission permission="LASSPASS_EDIT"><ClasificarCredencialesPage /></RequireModulePermission>} />
            <Route
              path="recursos/factory-prompts"
              element={<RequireModulePermission permission="FACTORY_PROMPTS_VIEW"><FactoryPromptsPage /></RequireModulePermission>}
            />
            <Route
              path="recursos/factory-prompts/factory"
              element={<RequireModulePermission permission="FACTORY_PROMPTS_FACTORY_RUN"><FactoryIaPage /></RequireModulePermission>}
            />
            <Route
              path="recursos/factory-prompts/factory/:runId"
              element={<RequireModulePermission permission="FACTORY_PROMPTS_VIEW"><FactoryIaPage /></RequireModulePermission>}
            />
            <Route
              path="recursos/factory-prompts/factory/:runId/proceso"
              element={<RequireModulePermission permission="FACTORY_PROMPTS_TRACE_VIEW"><FactoryTracePage /></RequireModulePermission>}
            />

            {/* AJUSTES */}
            <Route path="ajustes" element={<AjustesPage />} />
            <Route path="ajustes/mi-cuenta" element={<MiCuentaPage />} />
            <Route path="ajustes/integraciones" element={<IntegracionesSettingsPage />} />
            <Route path="ajustes/accesos-empleados" element={<RequireSuperAdmin><AccesosEmpleadosPage /></RequireSuperAdmin>} />
            <Route path="ajustes/usuarios" element={<RequireSuperAdmin><UsersPage /></RequireSuperAdmin>} />
            <Route path="ajustes/seguridad" element={<RequireSuperAdmin><SeguridadSistemaPage /></RequireSuperAdmin>} />
            <Route path="ajustes/registro" element={<RequireSuperAdmin><RegistroActividadPage /></RequireSuperAdmin>} />
            <Route path="ajustes/estado" element={<RequireSuperAdmin><EstadoSistemaPage /></RequireSuperAdmin>} />

            {/* Legacy module routes (kept for backward compatibility) */}
            <Route path="sistema/secrets" element={<Navigate to="/recursos/lasspass" replace />} />
            <Route path="sistema/usuarios" element={<RequireSuperAdmin><UsersPage /></RequireSuperAdmin>} />
            <Route path="sistema/permisos" element={<RequireSuperAdmin><PermissionsPage /></RequireSuperAdmin>} />
            <Route path="sistema/integraciones" element={<RequireSuperAdmin><IntegrationsCatalog /></RequireSuperAdmin>} />
            <Route path="sistema/auditoria" element={<RequireSuperAdmin><UnifiedAuditPage /></RequireSuperAdmin>} />
            <Route path="sistema/estado" element={<RequireSuperAdmin><SystemPage /></RequireSuperAdmin>} />

            {/* Legacy redirects */}
            {Object.entries(legacyModuleAliases).map(([from, to]) => (
              <Route key={from} path={from.slice(1)} element={<Navigate to={to} replace />} />
            ))}

            {/* Other routes */}
            <Route path="vaults" element={<Navigate to="/recursos/lasspass" replace />} />
            <Route path="vaults/:vaultId" element={<RequireModulePermission permission="LASSPASS_VIEW"><VaultPage /></RequireModulePermission>} />
            <Route path="items/:itemId" element={<RequireModulePermission permission="LASSPASS_VIEW"><ItemDetailPage /></RequireModulePermission>} />
            <Route path="items/new" element={<RequireModulePermission permission="LASSPASS_EDIT"><ItemEditPage /></RequireModulePermission>} />
            <Route path="items/:itemId/edit" element={<RequireModulePermission permission="LASSPASS_EDIT"><ItemEditPage /></RequireModulePermission>} />
            <Route path="items/:itemId/versions" element={<RequireModulePermission permission="LASSPASS_VIEW"><VersionsPage /></RequireModulePermission>} />
            <Route path="shared" element={<RequireModulePermission permission="LASSPASS_VIEW"><SharesPage /></RequireModulePermission>} />
            <Route path="import" element={<RequireModulePermission permission="LASSPASS_EDIT"><ImportPage /></RequireModulePermission>} />
            <Route path="security" element={<Navigate to="/ajustes/seguridad" replace />} />
            <Route path="activity" element={<Navigate to="/ajustes/registro" replace />} />
            <Route path="devices" element={<DevicesPage />} />
            <Route path="settings" element={<Navigate to="/ajustes" replace />} />
            <Route path="settings/recovery" element={<RecoverySetupPage />} />
            <Route path="admin" element={<RequireSuperAdmin><AdminPage /></RequireSuperAdmin>} />
            <Route path="mcp" element={<MCPIntegrationsPage />} />
            <Route path="projects" element={<Navigate to="/recursos/lasspass" replace />} />
            <Route path="recovery/setup" element={<RecoverySetupPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {/* Setup Center (SUPERADMIN only) */}
      {isSA && <SetupCenter open={setupOpen} onClose={() => setSetupOpen(false)} />}
    </>
  );
}